import config from 'const/config'
import routes from 'const/routes'
import { GlobalAppStyles } from 'globalStyles'
import { isBrandRefreshEnabled } from 'helpers/getBrandRefreshPhase'
import { IVAChat } from 'iva-react-web-ui'
import React, { ReactNode } from 'react'
import { ThemeProvider } from 'styled-components'
import theme from 'theme'
import DisasterAlertBanner from 'ui/atoms/DisasterAlertBanner'
import Header from 'ui/atoms/Header'
import { selectedAccountAtom } from 'store/atoms/selectedAccount.atom'
import { useSetAtom } from 'jotai'
import { AppWrapper, Footer } from './subcomponents'

interface DashboardLayoutProps {
  showRunningSections: boolean
  handleLanguageChange: () => void
  children: ReactNode
  locationPathname: string
  selectedAccount: string | null
  ivaUserMetadata: unknown
}

/**
 * DashboardLayout component for the main dashboard shell.
 * Applies theming, global styles, and conditionally renders header, footer, and IVA chat.
 */
const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  showRunningSections,
  handleLanguageChange,
  children,
  locationPathname,
  selectedAccount,
  ivaUserMetadata
}) => {
  const setSelectedAccount = useSetAtom(selectedAccountAtom)
  const activeTheme = {
    ...theme,
    ...(isBrandRefreshEnabled() && {
      body: {
        ...theme.body,
        background: 'var(--syfds-color-background-page-default)',
        backgroundColor: 'var(--syfds-color-background-page-default)'
      }
    })
  }

  // Ensure ivaUserMetadata is always an array for safe access
  const safeIvaUserMetadata = Array.isArray(ivaUserMetadata)
    ? ivaUserMetadata
    : []
  // Use selected account or fallback to first enterpriseId in metadata
  const currentEnterpriseId =
    selectedAccount || (safeIvaUserMetadata[0]?.enterpriseId ?? '')
  const isLWSErrorPage = locationPathname === routes.LWS_ERROR
  return (
    // Provide theme to all child components
    <ThemeProvider theme={activeTheme}>
      {/* Apply global styles only when dashboard sections are shown */}
      {showRunningSections && (
        <GlobalAppStyles isLWSErrorPage={isLWSErrorPage} />
      )}
      <AppWrapper data-test="dashboard">
        {/* Show header if running sections are enabled */}
        {showRunningSections && (
          <>
            <DisasterAlertBanner />
            <Header handleLanguageChange={handleLanguageChange} />
          </>
        )}
        {/* Main dashboard content */}
        {children}
        {/* Show footer if running sections are enabled */}
        {showRunningSections && <Footer />}
        {/* Render IVA chat widget when not on logout route */}
        {locationPathname !== routes.LOGOUT && (
          <IVAChat
            apiHostName={config.IVA_HOST}
            userMetadata={{
              currentEnterpriseId,
              platformInformation: safeIvaUserMetadata
            }}
            isLoggedIn
            showIvaChatIcon
            platform="VISTA"
            handleIVAAccountId={(selectedAccountId) => {
              // Retain existing context when IVA passes null/undefined (ROK-6166 AC #4).
              if (selectedAccountId) {
                setSelectedAccount(selectedAccountId)
              }
            }}
          />
        )}
      </AppWrapper>
    </ThemeProvider>
  )
}

export default DashboardLayout
